import React, { useState } from 'react';
import { verificationAPI } from '../../services/api';
import Navbar from '../../components/Navbar';
import './Verify.css';

const VerifyGSTIN = () => {
  const [gstin, setGstin] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showFilingSection, setShowFilingSection] = useState(false);
  const [selectedFY, setSelectedFY] = useState('');
  const [filteredFilingData, setFilteredFilingData] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setResult(null);
    setShowFilingSection(false);
    setSelectedFY('');
    setFilteredFilingData(null);

    try {
      const response = await verificationAPI.verifyGSTIN(gstin.toUpperCase());
      setResult(response);
    } catch (err) {
      setError(err.response?.data?.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleShowFilingTable = () => {
    setShowFilingSection(true);
  };

  const handleSearchFiling = () => {
    if (!selectedFY || !result?.data?.filing_details) {
      return;
    }

    const filtered = {
      gstr3b: result.data.filing_details.gstr3b?.filter(f => f.financialYear === selectedFY) || [],
      gstr1: result.data.filing_details.gstr1?.filter(f => f.financialYear === selectedFY) || [],
      gstr9: result.data.filing_details.gstr9?.filter(f => f.financialYear === selectedFY) || [],
      gstr9c: result.data.filing_details.gstr9c?.filter(f => f.financialYear === selectedFY) || []
    };

    setFilteredFilingData(filtered);
  };

  // Extract unique financial years from filing data
  const getFinancialYears = () => {
    if (!result?.data?.filing_details) return [];
    
    const years = new Set();
    result.data.filing_details.gstr3b?.forEach(f => years.add(f.financialYear));
    result.data.filing_details.gstr1?.forEach(f => years.add(f.financialYear));
    result.data.filing_details.gstr9?.forEach(f => years.add(f.financialYear));
    result.data.filing_details.gstr9c?.forEach(f => years.add(f.financialYear));
    
    return Array.from(years).sort().reverse();
  };

  return (
    <div className="verify-page">
      <Navbar />
      
      <div className="verify-container">
        <div className="verify-card">
          <h2>📋 GSTIN Verification</h2>
          <p className="verify-description">
            Verify GST Identification Number (GSTIN) details
          </p>

          <form onSubmit={handleSubmit} className="verify-form">
            <div className="form-group">
              <label>GSTIN</label>
              <input
                type="text"
                value={gstin}
                onChange={(e) => setGstin(e.target.value.toUpperCase())}
                placeholder="99AAAAA9999A9Z9"
                maxLength="15"
                required
                pattern="[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[0-9]{1}[A-Z]{1}[0-9]{1}"
                title="GSTIN format: 99AAAAA9999A9Z9"
              />
              <small>Format: 2 digits, 5 letters, 4 digits, 1 letter, 1 digit, 1 letter, 1 digit</small>
            </div>

            {error && <div className="error-message">{error}</div>}

            <button type="submit" className="verify-button" disabled={loading}>
              {loading ? 'Verifying...' : 'Verify GSTIN'}
            </button>
          </form>

          {result && (
            <div className="result-card">
              <h3>Verification Result</h3>
              <div className={`result-status ${result.success ? 'success' : 'failed'}`}>
                {result.success ? '✓ Verification Successful' : '✗ Verification Failed'}
              </div>
              
              {result.data && (
                <div className="result-details">
                  <table className="result-table">
                    <tbody>
                      {/* Basic Information */}
                      <tr>
                        <td><strong>GSTIN</strong></td>
                        <td>{result.data.gstin || '–'}</td>
                      </tr>
                      <tr>
                        <td><strong>Legal Name of Business</strong></td>
                        <td>{result.data.legal_name || '–'}</td>
                      </tr>
                      <tr>
                        <td><strong>Trade Name of Business</strong></td>
                        <td>{result.data.trade_name || '–'}</td>
                      </tr>
                      <tr>
                        <td><strong>Date of Registration</strong></td>
                        <td>{result.data.registration_date || '–'}</td>
                      </tr>
                      <tr>
                        <td><strong>Constitution of Business</strong></td>
                        <td>{result.data.constitution || '–'}</td>
                      </tr>
                      <tr>
                        <td><strong>GSTIN Status</strong></td>
                        <td>{result.data.status || '–'}</td>
                      </tr>
                      <tr>
                        <td><strong>Tax Payer Type</strong></td>
                        <td>{result.data.taxpayer_type || '–'}</td>
                      </tr>
                      <tr>
                        <td><strong>Principal Place Address</strong></td>
                        <td>{result.data.principal_place_address || '–'}</td>
                      </tr>
                      
                      {/* Administrative Office */}
                      {(result.data.admin_state || result.data.admin_zone || result.data.admin_commissionerate || result.data.admin_division || result.data.admin_range) && (
                        <>
                          <tr>
                            <td colSpan="2"><strong style={{fontSize: '1.1em', marginTop: '15px', display: 'block'}}>Administrative Office</strong></td>
                          </tr>
                          {result.data.admin_state && (
                            <tr>
                              <td><strong>State</strong></td>
                              <td>{result.data.admin_state}</td>
                            </tr>
                          )}
                          {result.data.admin_zone && (
                            <tr>
                              <td><strong>Zone</strong></td>
                              <td>{result.data.admin_zone}</td>
                            </tr>
                          )}
                          {result.data.admin_commissionerate && (
                            <tr>
                              <td><strong>Commissionerate</strong></td>
                              <td>{result.data.admin_commissionerate}</td>
                            </tr>
                          )}
                          {result.data.admin_division && (
                            <tr>
                              <td><strong>Division</strong></td>
                              <td>{result.data.admin_division}</td>
                            </tr>
                          )}
                          {result.data.admin_range && (
                            <tr>
                              <td><strong>Range</strong></td>
                              <td>{result.data.admin_range}</td>
                            </tr>
                          )}
                        </>
                      )}
                      
                      {/* Other Office */}
                      {(result.data.other_state || result.data.other_zone || result.data.other_division || result.data.other_charge) && (
                        <>
                          <tr>
                            <td colSpan="2"><strong style={{fontSize: '1.1em', marginTop: '15px', display: 'block'}}>Other Office</strong></td>
                          </tr>
                          {result.data.other_state && (
                            <tr>
                              <td><strong>State</strong></td>
                              <td>{result.data.other_state}</td>
                            </tr>
                          )}
                          {result.data.other_zone && (
                            <tr>
                              <td><strong>Zone</strong></td>
                              <td>{result.data.other_zone}</td>
                            </tr>
                          )}
                          {result.data.other_division && (
                            <tr>
                              <td><strong>Division</strong></td>
                              <td>{result.data.other_division}</td>
                            </tr>
                          )}
                          {result.data.other_charge && (
                            <tr>
                              <td><strong>Charge</strong></td>
                              <td>{result.data.other_charge}</td>
                            </tr>
                          )}
                        </>
                      )}
                      
                      {/* Aadhaar and eKYC */}
                      {result.data.aadhaar_authenticated && (
                        <tr>
                          <td><strong>Whether Aadhaar Authenticated?</strong></td>
                          <td>{result.data.aadhaar_authenticated} {result.data.aadhaar_auth_date && `(On ${result.data.aadhaar_auth_date})`}</td>
                        </tr>
                      )}
                      {result.data.ekyc_verified && (
                        <tr>
                          <td><strong>Whether e-KYC Verified?</strong></td>
                          <td>{result.data.ekyc_verified}</td>
                        </tr>
                      )}
                      
                      {/* Business Activity */}
                      {result.data.core_business_activity && (
                        <tr>
                          <td><strong>Nature Of Core Business Activity</strong></td>
                          <td>{result.data.core_business_activity}</td>
                        </tr>
                      )}
                      {result.data.business_activities && (
                        <tr>
                          <td><strong>Nature of Business Activities</strong></td>
                          <td>{result.data.business_activities}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  
                  {/* HSN Codes for Goods and Services */}
                  {((result.data.hsn_goods && result.data.hsn_goods.length > 0) || (result.data.hsn_services && result.data.hsn_services.length > 0)) && (
                    <div style={{marginTop: '20px'}}>
                      <h4>Dealing In Goods and Services</h4>
                      <table className="result-table">
                        <thead>
                          <tr>
                            <th colSpan="2">Goods</th>
                            <th colSpan="2">Services</th>
                          </tr>
                          <tr>
                            <th>HSN</th>
                            <th>Description</th>
                            <th>HSN</th>
                            <th>Description</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Array.from({length: Math.max(result.data.hsn_goods?.length || 0, result.data.hsn_services?.length || 0)}).map((_, index) => (
                            <tr key={index}>
                              <td>{result.data.hsn_goods?.[index]?.hsn || ''}</td>
                              <td>{result.data.hsn_goods?.[index]?.description || ''}</td>
                              <td>{result.data.hsn_services?.[index]?.hsn || ''}</td>
                              <td>{result.data.hsn_services?.[index]?.description || ''}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <p style={{fontSize: '0.9em', marginTop: '10px', fontStyle: 'italic', color: '#666'}}>
                        HSN: Harmonized System of Nomenclature of Goods and Services
                      </p>
                    </div>
                  )}
                  
                  {/* Filing Details Section with Button */}
                  {result.data.filing_details && (result.data.filing_details.gstr3b?.length > 0 || result.data.filing_details.gstr1?.length > 0) && (
                    <div style={{marginTop: '20px'}}>
                      <button 
                        onClick={handleShowFilingTable}
                        className="verify-button"
                        style={{marginBottom: '15px', width: 'auto', padding: '10px 20px'}}
                      >
                        Show Filing Table
                      </button>
                      
                      {showFilingSection && (
                        <>
                          <div style={{marginBottom: '15px', display: 'flex', gap: '10px', alignItems: 'center'}}>
                            <select 
                              value={selectedFY}
                              onChange={(e) => setSelectedFY(e.target.value)}
                              style={{
                                padding: '8px 12px',
                                borderRadius: '4px',
                                border: '1px solid #ddd',
                                fontSize: '14px'
                              }}
                            >
                              <option value="">Select Financial Year</option>
                              {getFinancialYears().map(fy => (
                                <option key={fy} value={fy}>{fy}</option>
                              ))}
                            </select>
                            <button 
                              onClick={handleSearchFiling}
                              className="verify-button"
                              style={{width: 'auto', padding: '8px 20px'}}
                              disabled={!selectedFY}
                            >
                              Search
                            </button>
                          </div>
                          
                          {filteredFilingData && (
                            <>
                              {filteredFilingData.gstr3b?.length > 0 && (
                                <div style={{marginBottom: '15px'}}>
                                  <h5>Filing details for GSTR3B</h5>
                                  <table className="result-table">
                                    <thead>
                                      <tr>
                                        <th>Financial Year</th>
                                        <th>Tax Period</th>
                                        <th>Date of filing</th>
                                        <th>Status</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {filteredFilingData.gstr3b.map((filing, index) => (
                                        <tr key={index}>
                                          <td>{filing.financialYear}</td>
                                          <td>{filing.taxPeriod}</td>
                                          <td>{filing.dateOfFiling}</td>
                                          <td>{filing.status}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                              {filteredFilingData.gstr1?.length > 0 && (
                                <div style={{marginBottom: '15px'}}>
                                  <h5>Filing details for GSTR-1/IFF</h5>
                                  <table className="result-table">
                                    <thead>
                                      <tr>
                                        <th>Financial Year</th>
                                        <th>Tax Period</th>
                                        <th>Date of filing</th>
                                        <th>Status</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {filteredFilingData.gstr1.map((filing, index) => (
                                        <tr key={index}>
                                          <td>{filing.financialYear}</td>
                                          <td>{filing.taxPeriod}</td>
                                          <td>{filing.dateOfFiling}</td>
                                          <td>{filing.status}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                              {filteredFilingData.gstr9?.length > 0 && (
                                <div style={{marginBottom: '15px'}}>
                                  <h5>Filing details for GSTR9</h5>
                                  <table className="result-table">
                                    <thead>
                                      <tr>
                                        <th>Financial Year</th>
                                        <th>Tax Period</th>
                                        <th>Date of filing</th>
                                        <th>Status</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {filteredFilingData.gstr9.map((filing, index) => (
                                        <tr key={index}>
                                          <td>{filing.financialYear}</td>
                                          <td>{filing.taxPeriod}</td>
                                          <td>{filing.dateOfFiling}</td>
                                          <td>{filing.status}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                              {filteredFilingData.gstr9c?.length > 0 && (
                                <div>
                                  <h5>Filing details for GSTR9C</h5>
                                  <table className="result-table">
                                    <thead>
                                      <tr>
                                        <th>Financial Year</th>
                                        <th>Tax Period</th>
                                        <th>Date of filing</th>
                                        <th>Status</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {filteredFilingData.gstr9c.map((filing, index) => (
                                        <tr key={index}>
                                          <td>{filing.financialYear}</td>
                                          <td>{filing.taxPeriod}</td>
                                          <td>{filing.dateOfFiling}</td>
                                          <td>{filing.status}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </>
                          )}
                        </>
                      )}
                    </div>
                  )}
                  
                  {/* Filing Frequency */}
                  {result.data.filing_frequency && result.data.filing_frequency.length > 0 && (
                    <div style={{marginTop: '20px'}}>
                      <h4>Return Filing Frequency</h4>
                      <table className="result-table">
                        <thead>
                          <tr>
                            <th>Financial Year</th>
                            <th>Quarter</th>
                            <th>Frequency</th>
                            <th>Quarter</th>
                            <th>Frequency</th>
                            <th>Quarter</th>
                            <th>Frequency</th>
                            <th>Quarter</th>
                            <th>Frequency</th>
                          </tr>
                        </thead>
                        <tbody>
                          {result.data.filing_frequency.map((freq, index) => (
                            <tr key={index}>
                              <td>{freq.financialYear}</td>
                              <td>{freq.q1Quarter}</td>
                              <td>{freq.q1Frequency}</td>
                              <td>{freq.q2Quarter}</td>
                              <td>{freq.q2Frequency}</td>
                              <td>{freq.q3Quarter}</td>
                              <td>{freq.q3Frequency}</td>
                              <td>{freq.q4Quarter}</td>
                              <td>{freq.q4Frequency}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <p style={{fontSize: '0.9em', marginTop: '10px', fontStyle: 'italic'}}>
                        * Not Applicable implies either the taxpayer type is not registered or Cancelled or Composition.
                      </p>
                    </div>
                  )}
                </div>
              )}
              
              <p className="credits-deducted">
                Credits Deducted: ₹{result.creditsDeducted || 0}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyGSTIN;
